import React, { useEffect, useState } from "react";
import DataTable from "../../../../components/common/DataTable";
import ListExportToolbar from "../../components/list/ListExportToolbar";
import { makeGrandTotalSummary } from "../../components/list/GrandTotalSummary";
import { useListStockBalancesQuery } from "../../stockApi";
import { useGetProductGroupsQuery } from "../../../masters/products/productsApi";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { useHasPermission } from "../../../auth/useClaims";
import StockBalanceTallyPanel from "../components/StockBalanceTallyPanel";

const STOCK_LEVEL_OPTIONS = [
  { value: "", label: "All Stock" },
  { value: "HIDE_ZERO", label: "Hide Zero Stock" },
  { value: "ONLY_ZERO", label: "Only Zero Stock" },
];

// The list is capped at this many rows per request. A whole branch is several
// thousand products, so the page says when it is showing a truncated list and
// asks for a group to be picked.
const PAGE_SIZE = 500;

// Read-only - deliberately no QR/barcode action here. The API generates a
// barcode per received Stock-In line/batch, not per product balance (that's
// what the handheld scanning device work will actually read later), so
// barcode printing lives on Stock-In detail instead of here.
//
// Every product is listed (Batch192, 2026-09-22), not only the ones that have
// moved: a never-moved product shows qty 0 and "no movement yet". That makes
// the list thousands of rows for a branch, so the Group and Stock Level
// filters are server-side (?productGroupId=&stockLevel=), not applied over one
// page in the browser as before. Group ids are branch-local, so the Group
// dropdown needs a branch selected in the global branch filter.
//
// The LIST has no date range - it is a current snapshot (no dateFrom/dateTo on
// the API). The two dates on the page only set the period of the "movement
// check" that opens under a row (StockBalanceTallyPanel): opening + in - out =
// closing, checked against the stock on hand.
const StockBalancesPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [productGroupId, setProductGroupId] = useState("");
  const [stockLevel, setStockLevel] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  // Movement-check period: this month so far. Read by the expanded row only.
  const todayIso = new Date().toISOString().slice(0, 10);
  const [checkFrom, setCheckFrom] = useState(`${todayIso.slice(0, 8)}01`);
  const [checkTo, setCheckTo] = useState(todayIso);
  // The check reads the Stock Movement Report endpoint, which has its own permission.
  const canCheckMovements = useHasPermission("Reports.StockMovement");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Groups are branch-wise (same as Products' own group filter, see
  // ProductsList.jsx): the dropdown shows the selected branch's groups by id.
  const { data: allGroups = [] } = useGetProductGroupsQuery();
  const scopedGroups = selectedBranchId
    ? allGroups.filter((g) => g.branchId === selectedBranchId).sort((a, b) => (a.groupName || "").localeCompare(b.groupName || ""))
    : [];

  // A group pick belongs to one branch - drop it when the branch changes or is cleared.
  useEffect(() => {
    setProductGroupId("");
  }, [selectedBranchId]);

  const { data, isLoading, isFetching } = useListStockBalancesQuery({
    branchId: selectedBranchId ?? undefined,
    productGroupId: productGroupId || undefined,
    search: search || undefined,
    stockLevel: stockLevel || undefined,
    includeInactive: includeInactive || undefined,
    pageSize: PAGE_SIZE,
  });

  const items = data?.items || [];
  const totalCount = data?.totalCount ?? items.length;
  const isTruncated = totalCount > items.length;
  const neverMoved = items.filter((r) => !r.lastMovementType).length;

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const groupName = scopedGroups.find((g) => String(g.groupId) === String(productGroupId))?.groupName;

  const exportColumns = [
    { header: "Product", value: (r) => `${r.productName} (${r.productCode})` },
    { header: "Group", value: (r) => r.productGroupName || "-" },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Current Qty", value: (r) => Number(r.currentQty || 0).toFixed(2), align: "right" },
    { header: "Last Movement", value: (r) => r.lastMovementType?.replaceAll("_", " ") || "no movement yet" },
    { header: "Last Movement Date", value: (r) => (r.lastMovementDate ? new Date(r.lastMovementDate).toLocaleString() : "-") },
  ];

  const columns = [
    {
      title: "Product",
      dataIndex: "productName",
      render: (v, r) => (
        <>
          {v} ({r.productCode})
          {!r.isActive && <span className="badge badge-linedanger ms-2">Inactive</span>}
        </>
      ),
    },
    { title: "Group", dataIndex: "productGroupName", render: (v) => v || "-" },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Current Qty", dataIndex: "currentQty", render: (v) => Number(v || 0).toFixed(2) },
    {
      title: "Last Movement",
      dataIndex: "lastMovementType",
      render: (v) => (v ? v.replaceAll("_", " ") : <span className="text-muted">no movement yet</span>),
    },
    { title: "Last Movement Date", dataIndex: "lastMovementDate", render: (v) => (v ? new Date(v).toLocaleString() : "-") },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock Balances</h4>
              <h6>
                Current stock on hand for every product, moved or not
                {canCheckMovements ? " - click a product to check it against its movements" : ""}
              </h6>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search product name or code"
                    className="form-control form-control-sm formsearch"
                    style={{ minWidth: 220, height: 34 }}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <select
                  className="form-control form-control-sm"
                  style={{ width: 200, height: 34 }}
                  value={productGroupId}
                  onChange={(e) => setProductGroupId(e.target.value)}
                  disabled={!selectedBranchId}
                  title={selectedBranchId ? "Product group" : "Select a branch in the top bar to filter by group"}
                >
                  <option value="">{selectedBranchId ? "All Groups" : "All Groups (pick a branch to filter)"}</option>
                  {scopedGroups.map((g) => (
                    <option key={`${g.branchId}-${g.groupId}`} value={g.groupId}>{g.groupName}</option>
                  ))}
                </select>
                <select className="form-control form-control-sm" style={{ width: 170, height: 34 }} value={stockLevel} onChange={(e) => setStockLevel(e.target.value)}>
                  {STOCK_LEVEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="form-check form-switch mb-0">
                  <input type="checkbox" className="form-check-input" id="stockBalancesInactive" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
                  <label className="form-check-label" htmlFor="stockBalancesInactive">Include inactive products</label>
                </div>
                {canCheckMovements && (
                  <div className="d-flex align-items-center gap-1" title="Period used by the movement check that opens under a product">
                    <span className="text-muted small">Movement check</span>
                    <input type="date" className="form-control form-control-sm" style={{ width: 140, height: 34 }} value={checkFrom} max={checkTo} onChange={(e) => e.target.value && setCheckFrom(e.target.value)} />
                    <span className="text-muted small">to</span>
                    <input type="date" className="form-control form-control-sm" style={{ width: 140, height: 34 }} value={checkTo} min={checkFrom} max={todayIso} onChange={(e) => e.target.value && setCheckTo(e.target.value)} />
                  </div>
                )}
                <ListExportToolbar
                  title="Stock Balances"
                  subtitleLines={[
                    `Branch: ${branchCode || "All"}`,
                    `Group: ${groupName || "All"}`,
                    `As of: ${new Date().toLocaleDateString()}`,
                  ]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={items}
                  fileName="StockBalances"
                  groupBy={(r) => r.productGroupName || "Ungrouped"}
                />
              </div>
            </div>

            {isTruncated && (
              <div className="alert alert-warning py-2 px-3 mb-2 mt-2">
                Showing the first {items.length} of {totalCount} products - pick a group, a branch or a search to see a complete list.
              </div>
            )}
            {!isTruncated && items.length > 0 && (
              <div className="text-muted small mt-2">
                {totalCount} product{totalCount === 1 ? "" : "s"}
                {neverMoved > 0 ? `, ${neverMoved} with no stock movement yet` : ""}
              </div>
            )}

            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={items}
                rowKey="productRecordGuid"
                loading={isLoading || isFetching}
                expandable={canCheckMovements ? {
                  expandRowByClick: true,
                  expandedRowRender: (r) => <StockBalanceTallyPanel balance={r} dateFrom={checkFrom} dateTo={checkTo} />,
                } : undefined}
                summary={makeGrandTotalSummary(columns, items, ["currentQty"], "productName")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockBalancesPage;
